/**
 * One icon, drawn inline so it can take the colour of the text around it.
 *
 * ## Why inline, rather than an `<img>` or a CSS mask
 *
 * Both of the obvious approaches were tried and both fail, each on a different
 * half of the icon set:
 *
 * - **`<img src="icon.svg">`** paints the file's own colours and inherits
 *   nothing. Our icons are drawn with `currentColor`, which in a standalone SVG
 *   document resolves to **black** - invisible on the felt.
 * - **A CSS mask** fixes the colour but reads only the alpha channel, so every
 *   opaque shape becomes one silhouette. The four "pair of cards" icons are
 *   **two-tone** - a filled card with its rank and pips knocked out in white -
 *   and as a mask the knockouts disappear and the card becomes a solid block.
 *
 * Inlining is the only one that serves both: `fill="currentColor"` inherits the
 * surrounding colour, and `fill="white"` stays white, because it is real SVG in
 * the document rather than a picture of one.
 *
 * ## Where the markup comes from
 *
 * `import.meta.glob` reads every file in `../icons/` at **build time**, so the
 * set is whatever is in that directory - adding an icon is adding a file, with
 * nothing to register. They are bundled rather than fetched, which for eighteen
 * files of one to four KB is fewer requests and no flash of a missing glyph.
 *
 * `dangerouslySetInnerHTML` is doing the inlining, and the danger it is named
 * for does not apply: the strings are our own files, read at build time, and no
 * value from a user or a server can reach this.
 */

/** Every icon in `src/icons/`, as source, resolved at build time. */
const SOURCES = import.meta.glob('../icons/*.svg', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

/** Keyed by bare name: `../icons/crown.svg` becomes `crown`. */
const BY_NAME: Record<string, string> = {};
for (const [path, source] of Object.entries(SOURCES)) {
  const name = path.split('/').pop()?.replace('.svg', '');
  if (name) BY_NAME[name] = source;
}

export type IconName =
  // Interface
  | 'play'
  | 'join-group'
  | 'people'
  | 'mic'
  | 'gamepad'
  | 'heart'
  /* The same heart, stroked: empty is an offer, full is an answer. */
  | 'heart-outline'
  | 'chevron-right'
  | 'close'
  | 'info'
  | 'search'
  | 'link'
  | 'share'
  | 'dice'
  | 'mail'
  | 'message'
  | 'eye'
  | 'repeat'
  | 'clock'
  /* Three rising bars, for how hard a game is to learn. */
  | 'difficulty'
  | 'mic-off'
  | 'speaker'
  | 'speaker-off'
  | 'menu-dots'
  // Suits and game marks
  | 'spade'
  | 'diamond'
  | 'crown'
  | 'pair-of-aces'
  | 'pair-of-cards'
  | 'pair-of-eights'
  | 'pair-of-sevens';

export function Icon({ name, className }: { name: IconName; className?: string | undefined }) {
  const source = BY_NAME[name];
  // A missing file is a typo in a name, and drawing nothing is better than
  // drawing a broken-image glyph in the middle of a button.
  if (!source) return null;

  return (
    <span
      className={className ? `icon ${className}` : 'icon'}
      /*
       * Always hidden from assistive tech. Every icon here sits beside a real
       * label, and one that announced itself would make a screen reader say
       * everything twice. Used alone, the *button* carries the label.
       */
      aria-hidden="true"
      dangerouslySetInnerHTML={{ __html: source }}
    />
  );
}
